import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessagesSquare } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/SEO";

export default function Community() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const firstLoad = useRef(true);

  useEffect(() => {
    api.get("/membership/status").then((r) => setStatus(r.data));
  }, [user]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages");
      setMessages(data);
      if (firstLoad.current) {
        firstLoad.current = false;
        setTimeout(() => bottomRef.current?.scrollIntoView({ block: "nearest" }), 100);
      }
    } catch { /* not a member or transient */ }
  }, []);

  useEffect(() => {
    if (!status?.member) return;
    load();
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
  }, [status, load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/chat/messages", { text });
      setMessages((m) => [...m, data]);
      setText("");
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  if (status === null) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  if (!status.member) {
    return (
      <div className="silk-hero min-h-[70vh] flex items-center justify-center px-5">
        <SEO title="Community Chat — O.R.E.C" description="One room, all of us — the O.R.E.C community chat for members." />
        <div className="silk-card rounded-lg p-10 max-w-md w-full text-center" data-testid="community-locked">
          <MessagesSquare size={36} className="text-primary mx-auto mb-4" />
          <h1 className="font-display text-3xl text-foreground mb-3">The Community Chat</h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-7">
            One room, all of us — seekers and Leaders sharing what's working, what's beautiful, and what's next. Community Members only.
          </p>
          <Button className="rounded-full px-8" data-testid="community-join-cta" onClick={() => navigate(user ? "/membership" : "/register")}>
            {user ? "Become a Community Member" : "Join O.R.E.C first"}
          </Button>
          {status.launch_offer_active && (
            <p className="text-xs text-primary mt-4">Launch special — Community Membership is free for our founding year ✦</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="silk-hero min-h-[70vh] px-5 sm:px-8 py-12">
      <SEO title="Community Chat — O.R.E.C" description="One room, all of us — the O.R.E.C community chat for members." />
      <div className="max-w-2xl mx-auto flex flex-col" data-testid="community-chat" style={{ height: "calc(100vh - 220px)", minHeight: "440px" }}>
        <h1 className="font-display text-3xl text-foreground mb-2">Community Chat</h1>
        <p className="text-xs text-muted-foreground border border-border rounded-md px-3 py-2 mb-4" data-testid="community-notice">
          One room, all of us. Be kind — and please, no advertising. Leaders have their listings for that. ✦
        </p>
        <div className="silk-card rounded-lg flex-1 overflow-y-auto p-5 space-y-4" data-testid="community-messages">
          {messages.map((m) => (
            <div key={m.id} className={`max-w-[85%] ${m.user_id === user?.id ? "ml-auto text-right" : ""}`}>
              <p className="text-[11px] text-muted-foreground mb-1">
                {m.name}{m.role === "practitioner" ? " · Leader" : m.role === "admin" ? " · The Team" : ""}
              </p>
              <p className={`inline-block text-sm rounded-lg px-4 py-2.5 leading-relaxed ${m.user_id === user?.id ? "bg-primary text-primary-foreground" : "bg-background/70 border border-border text-foreground"}`}>
                {m.text}
              </p>
            </div>
          ))}
          {messages.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-12" data-testid="community-empty">
              It's quiet in here… be the first to say hello. ✦
            </p>
          )}
          <div ref={bottomRef} />
        </div>
        <form onSubmit={send} className="flex items-center gap-2 mt-4">
          <Input data-testid="community-input" value={text} maxLength={500} placeholder="Say something kind…"
            onChange={(e) => setText(e.target.value)} className="flex-1" />
          <Button type="submit" disabled={busy || !text.trim()} className="rounded-full px-5" data-testid="community-send-btn">
            <Send size={15} />
          </Button>
        </form>
      </div>
    </div>
  );
}
